import React from 'react';
import { Task, TaskStatus } from '@models/task.model';
import { TaskOptions, getTranslatedTaskStatusLabel } from '@utils/options';
import { formatForDisplay } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

interface SubtasksTableProps {
  existingSubtasks: Task[];
  subtasksToAdd: string[];
  subtasksToRemove: string[];
  onRemoveSubtask: (taskId: string) => void;
  task?: Task | null;
  availableTasksCount: number;
  allTasks: Task[];
}

export const SubtasksTable: React.FC<SubtasksTableProps> = ({
  existingSubtasks,
  subtasksToAdd,
  subtasksToRemove,
  onRemoveSubtask,
  task,
  availableTasksCount,
  allTasks
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  // The existingSubtasks already includes pending additions (calculated in parent),
  // so we don't need to add them again to avoid duplicates
  const allSubtasksToDisplay = existingSubtasks;

  // Show table if there are existing subtasks or pending additions (for create mode)
  const hasSubtasksToShow = allSubtasksToDisplay.length > 0;

  if (hasSubtasksToShow) {
    return (
      <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-h-64 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colName}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colStatus}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colStartTime}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colEndTime}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colTaskType}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                {tTaskDetail.general.colActions}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {allSubtasksToDisplay.map((subtask: Task, index: number) => {
              const translatedStatusLabel = subtask.status
                ? getTranslatedTaskStatusLabel(subtask.status, tTaskDetail)
                : undefined;
              const typeOption = TaskOptions.find(
                (opt) => opt.id === subtask.taskType,
              );

              // Check if this is a pending addition or removal
              const isPendingAddition = subtasksToAdd.includes(
                subtask.taskId || '',
              );
              const isPendingRemoval = subtasksToRemove.includes(
                subtask.taskId || '',
              );

              return (
                <tr
                  key={subtask.taskId || index}
                  className={
                    isPendingAddition
                      ? 'bg-green-50'
                      : isPendingRemoval
                        ? 'bg-red-50'
                        : ''
                  }>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      {subtask.name}
                      {isPendingAddition && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          {!task?.taskId ? tTaskDetail.general.willBeAdded : tTaskDetail.general.pendingAdd}
                        </span>
                      )}
                      {isPendingRemoval && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          {tTaskDetail.general.pendingRemove}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        subtask.status === TaskStatus.COMPLETE
                          ? 'bg-green-100 text-green-800'
                          : subtask.status === TaskStatus.ACTIVE
                            ? 'bg-blue-100 text-blue-800'
                            : subtask.status === TaskStatus.REJECTED
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}>
                      {translatedStatusLabel || subtask.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatForDisplay(subtask.startTime) || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatForDisplay(subtask.endTime) || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {typeOption?.name || subtask.taskType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      type="button"
                      onClick={() => onRemoveSubtask(subtask.taskId || '')}
                      className={`font-medium ${
                        isPendingAddition || isPendingRemoval
                          ? 'text-blue-600 hover:text-blue-900'
                          : 'text-red-600 hover:text-red-900'
                      }`}>
                      {isPendingRemoval
                        ? tTaskDetail.general.undoRemove
                        : tTaskDetail.general.remove}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (!task?.delegateOrganizationId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{tTaskDetail.general.noSubtasksAssigned}</p>
        {!task?.taskId && availableTasksCount > 0 && (
          <p className="text-sm mt-1">{tTaskDetail.general.clickAddSubtasksHint}</p>
        )}
        {!task?.taskId && availableTasksCount === 0 && (
          <p className="text-sm mt-1">{tTaskDetail.general.noAvailableTasksToAdd}</p>
        )}
        {task?.taskId && availableTasksCount === 0 && (
          <p className="text-sm mt-1">{tTaskDetail.general.noAvailableTasksToAdd}</p>
        )}
      </div>
    );
  }

  return null;
};